import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, Skull, AlertTriangle, Cpu, HardDrive, MemoryStick } from 'lucide-react';

interface Process {
  user: string;
  pid: number;
  cpu: number;
  mem: number;
  vsz: string;
  rss: string;
  command: string;
}

interface SystemStats {
  cpu: { percent: number; load: string; cores: number };
  memory: { percent: number; used: string; total: string };
  disk: { percent: number; used: string; total: string };
}

interface ProcessManagerProps {
  isOpen: boolean;
  onClose: () => void;
  serverUrl: string;
}

const ProcessManager: React.FC<ProcessManagerProps> = ({ isOpen, onClose, serverUrl }) => {
  const [processes, setProcesses] = useState<Process[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [killing, setKilling] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [procRes, statsRes] = await Promise.all([
        fetch(`${serverUrl}/api/processes?limit=15`),
        fetch(`${serverUrl}/api/system-stats`)
      ]);

      if (procRes.ok) {
        const data = await procRes.json();
        setProcesses(data.processes || []);
      }

      if (statsRes.ok) {
        const stats = await statsRes.json();
        setSystemStats(stats);
      }
    } catch (e) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  }, [serverUrl]);

  const killProcess = async (pid: number, force: boolean = false) => {
    setKilling(pid);
    try {
      const res = await fetch(`${serverUrl}/api/processes/kill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pid, signal: force ? 'SIGKILL' : 'SIGTERM' })
      });

      if (res.ok) {
        // Refresh after kill
        setTimeout(fetchData, 500);
      } else {
        const data = await res.json();
        setError(data.error || 'Kill failed');
      }
    } catch {
      setError('Kill request failed');
    }
    setKilling(null);
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
      const interval = setInterval(fetchData, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, fetchData]);

  if (!isOpen) return null;

  const getUsageColor = (pct: number) => {
    if (pct >= 80) return 'text-red-400';
    if (pct >= 50) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getBarColor = (pct: number) => {
    if (pct >= 80) return 'bg-red-500';
    if (pct >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-[700px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-cyan-400" />
            <span className="font-semibold text-white">Process Manager</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-1.5 hover:bg-gray-700 rounded transition-colors"
            >
              <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-700 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* System Stats Bar */}
        {systemStats && (
          <div className="grid grid-cols-3 gap-4 px-4 py-3 bg-gray-800/50 border-b border-gray-700">
            {/* CPU */}
            <div className="flex items-center gap-3">
              <Cpu className={`w-4 h-4 ${getUsageColor(systemStats.cpu.percent)}`} />
              <div className="flex-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">CPU</span>
                  <span className={getUsageColor(systemStats.cpu.percent)}>
                    {systemStats.cpu.percent}%
                  </span>
                </div>
                <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getBarColor(systemStats.cpu.percent)} transition-all`}
                    style={{ width: `${systemStats.cpu.percent}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Memory */}
            <div className="flex items-center gap-3">
              <MemoryStick className={`w-4 h-4 ${getUsageColor(systemStats.memory.percent)}`} />
              <div className="flex-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">RAM</span>
                  <span className={getUsageColor(systemStats.memory.percent)}>
                    {systemStats.memory.used}/{systemStats.memory.total}GB
                  </span>
                </div>
                <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getBarColor(systemStats.memory.percent)} transition-all`}
                    style={{ width: `${systemStats.memory.percent}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Disk */}
            <div className="flex items-center gap-3">
              <HardDrive className={`w-4 h-4 ${getUsageColor(systemStats.disk.percent)}`} />
              <div className="flex-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">Disk</span>
                  <span className={getUsageColor(systemStats.disk.percent)}>
                    {systemStats.disk.used}/{systemStats.disk.total}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getBarColor(systemStats.disk.percent)} transition-all`}
                    style={{ width: `${systemStats.disk.percent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-4 py-2 bg-red-900/30 border-b border-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-300">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Process List */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 sticky top-0">
              <tr className="text-gray-400 text-xs uppercase">
                <th className="px-4 py-2 text-left">PID</th>
                <th className="px-4 py-2 text-right">CPU%</th>
                <th className="px-4 py-2 text-right">MEM%</th>
                <th className="px-4 py-2 text-left">Command</th>
                <th className="px-4 py-2 text-center w-20">Kill</th>
              </tr>
            </thead>
            <tbody>
              {processes.map((proc) => (
                <tr
                  key={proc.pid}
                  className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                >
                  <td className="px-4 py-2 font-mono text-gray-300">{proc.pid}</td>
                  <td className={`px-4 py-2 text-right font-mono ${getUsageColor(proc.cpu)}`}>
                    {proc.cpu.toFixed(1)}
                  </td>
                  <td className={`px-4 py-2 text-right font-mono ${getUsageColor(proc.mem)}`}>
                    {proc.mem.toFixed(1)}
                  </td>
                  <td className="px-4 py-2 text-gray-400 truncate max-w-[300px]" title={proc.command}>
                    {proc.command}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => killProcess(proc.pid, false)}
                        disabled={killing === proc.pid}
                        className="p-1 hover:bg-yellow-600/30 rounded text-yellow-500 hover:text-yellow-400 transition-colors"
                        title="SIGTERM (graceful)"
                      >
                        <Skull className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => killProcess(proc.pid, true)}
                        disabled={killing === proc.pid}
                        className="p-1 hover:bg-red-600/30 rounded text-red-500 hover:text-red-400 transition-colors"
                        title="SIGKILL (force)"
                      >
                        <Skull className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {processes.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No processes found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-700 text-xs text-gray-500 flex justify-between">
          <span>Showing top 15 by memory usage</span>
          <span>Auto-refresh: 5s</span>
        </div>
      </div>
    </div>
  );
};

export default ProcessManager;
